import cn from 'classnames';
import Image, { StaticImageData } from 'next/image';
import { FC, forwardRef, PropsWithChildren, ReactNode, useState } from 'react';
import GradientSvg from './gradient';

import s from './box.module.scss';

export const Box: FC<
  PropsWithChildren<{
    inverted?: boolean;
    title: string;
    subtitle: string;
    image?: { src: string | StaticImageData; alt: string; width: number; height: number };
    imageHTML?: ReactNode;
  }>
> = ({ inverted, title, subtitle, image, imageHTML, children }) => {
  return (
    <div className="rounded-2xl relative">
      <div className={s['bg-container']}>
        <GradientSvg className="absolute hidden sm:block lg:bottom-0 lg:left-0 h-full lg:h-auto" />
        <div className={cn(s.grid, 'h-full')} />
      </div>
      <div
        className={cn(
          'flex xl:py-20 z-10 relative py-14 px-8 flex-col-reverse',
          inverted ? 'lg:flex-row-reverse xl:pr-20' : 'lg:flex-row xl:pl-20'
        )}
      >
        <div className="xl:flex xl:w-auto w-full flex-col justify-center lg:items-start xl:min-w-[466px] xl:max-w-[466px] flex-1">
          <p className="font-aeonik text-2xl md:text-3xl xl:text-4xl mb-3 xl:mb-2 text-black sm:text-white">
            {title}
          </p>
          <p className="text-black sm:text-white/70">{subtitle}</p>
          {children}
        </div>

        {image && (
          <Image
            src={image.src}
            width={image.width}
            height={image.height}
            alt={image.alt}
            quality={100}
            placeholder="blur"
            className={cn(
              inverted
                ? 'lg:-translate-x-10 3xl:-translate-x-18'
                : 'lg:translate-x-10 3xl:translate-x-18',
              'rounded-lg shadow-xl xl:shadow-super lg:max-w-[50%] xl:max-w-none lg:mb-0 mb-9 select-none'
            )}
          />
        )}

        {imageHTML}
      </div>
    </div>
  );
};

export const LoadOnHoverIframe = forwardRef<
  HTMLIFrameElement,
  { placeholder: React.ReactNode; eager?: boolean } & Omit<
    JSX.IntrinsicElements['iframe'],
    'placeholder'
  >
>(({ placeholder, eager = false, src }, iframeRef) => {
  const [hasHovered, setHasHovered] = useState(false);

  if (hasHovered || eager)
    return <iframe src={src} className="w-full h-full absolute inset-0 bg-black" ref={iframeRef} />;

  return (
    <div onMouseOver={() => setHasHovered(true)} className="w-full h-full">
      {placeholder}
    </div>
  );
});
